/* eslint react-hooks/exhaustive-deps: "warn" */
import { type CSSProperties, type KeyboardEvent, type ReactNode, useEffect, useRef, useState } from 'react';

import cn from 'classnames';

import { useInt } from 'hooks/useInt';
import { useIsomorphicLayoutEffect } from 'hooks/useIsomorphicLayoutEffect';

import { Flag } from 'ui/atoms/Flag/Flag';
import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { Section } from 'ui/molecules/Section/Section';

import styles from './GovernmentMandates.module.scss';

type Translate = (key: string) => string;

const RETRIEVED = 'retrieved 2026-07-27';

const Party = ({ code, mark }: { code?: string; mark?: string }) =>
  code ? <Flag code={code} className={styles.partyFlag} /> : <span className={styles.partyMark}>{mark ?? 'XX'}</span>;

const SpecHead = ({ title }: { title: string }) => {
  const { t } = useInt();

  return (
    <div className={styles.specHead}>
      <span className={styles.specHeadT}>{title}</span>
      <span className={styles.badge}>{t('Illustrative')}</span>
    </div>
  );
};

type ExclusionRow = { code?: string; mark?: string; name: string; role: string; result: 'ok' | 'flag'; label: string };

const ExclusionSpec = () => {
  const { t } = useInt();
  const rows: ExclusionRow[] = [
    { code: 'gb', name: 'SAMPLE MERIDIAN CIVILS LTD', role: 'Bidder', result: 'ok', label: 'clear' },
    { code: 'nl', name: 'SAMPLE HOLDINGS B.V.', role: 'Parent undertaking', result: 'ok', label: 'clear' },
    { code: 'lu', name: 'SAMPLE CAPITAL S.à r.l.', role: 'Parent undertaking', result: 'ok', label: 'clear' },
    {
      mark: 'XX',
      name: 'SAMPLE STATE INVESTMENT AUTHORITY',
      role: 'Ultimate parent',
      result: 'flag',
      label: 'state-owned',
    },
    { code: 'gb', name: 'SAMPLE GROUNDWORKS LTD', role: 'Named sub-contractor', result: 'ok', label: 'clear' },
    { mark: '·', name: 'A. VEIGA', role: 'Director', result: 'flag', label: 'PEP match' },
  ];

  return (
    <div className={styles.spec}>
      <SpecHead title={t('Exclusion check · Procurement Act 2023')} />
      {rows.map((row) => (
        <div className={styles.specRow} key={row.name}>
          <Party code={row.code} mark={row.mark} />
          <span className={styles.specName}>{row.name}</span>
          <span className={styles.specRole}>{row.role}</span>
          <span className={cn(styles.specResult, row.result === 'flag' && styles.specResultFlag)}>{row.label}</span>
        </div>
      ))}
      <div className={styles.specFoot}>6 parties checked · 4 registers · 2 flags raised · {RETRIEVED}</div>
    </div>
  );
};

const LADDER_PERCENTS = [100, 82, 76];
const LADDER_EFFECTIVE = (LADDER_PERCENTS.reduce((acc, p) => acc * (p / 100), 1) * 100).toFixed(2);

const LadderSpec = () => {
  const { t } = useInt();

  return (
    <div className={styles.spec}>
      <SpecHead title={t('Control computation · NSIA 2021')} />
      <div className={styles.ladder}>
        <div className={cn(styles.ladderRow, styles.depth0)}>
          <Party code="gb" />
          <span className={styles.ladderName}>
            SAMPLE VANTOR SYSTEMS LTD
            <i>Target · notifiable sector</i>
          </span>
          <span className={cn(styles.ladderPc, styles.ladderPcMut)}>—</span>
        </div>
        <div className={cn(styles.ladderRow, styles.depth1)}>
          <Party code="nl" />
          <span className={styles.ladderName}>
            SAMPLE NORTH HOLDINGS B.V.
            <i>Acquirer · layer 1</i>
          </span>
          <span className={styles.ladderPc}>100.00%</span>
        </div>
        <div className={cn(styles.ladderRow, styles.depth2)}>
          <Party code="lu" />
          <span className={styles.ladderName}>
            SAMPLE CAPITAL S.à r.l.
            <i>Layer 2</i>
          </span>
          <span className={styles.ladderPc}>82.00%</span>
        </div>
        <div className={cn(styles.ladderRow, styles.depth3, styles.ladderOwner)}>
          <Party mark="XX" />
          <span className={styles.ladderName}>
            SAMPLE STATE INVESTMENT AUTHORITY
            <i>Ultimate parent · state-owned</i>
          </span>
          <span className={styles.ladderPc}>76.00%</span>
        </div>
      </div>
      <div className={styles.ladderFoot}>
        <span className={styles.ladderEq}>100.00 × 82.00 × 76.00 =</span>
        <span className={styles.ladderOut}>
          effective control <b>{LADDER_EFFECTIVE}%</b>
        </span>
        <span className={styles.modBadge}>Modelled</span>
        <span className={styles.footRest}>above the 50% test · 4 registers</span>
      </div>
    </div>
  );
};

type TraceStep = {
  code?: string;
  mark?: string;
  name: string;
  role: string;
  status: string;
  stop?: boolean;
  into?: string;
};

const TraceSpec = () => {
  const { t } = useInt();
  const steps: TraceStep[] = [
    { code: 'ae', name: 'SAMPLE GULF INSTRUMENTS FZE', role: 'Stated end-user', status: 'active · registered' },
    {
      code: 'ae',
      name: 'SAMPLE GULF TRADING FZE',
      role: 'Majority shareholder',
      status: 'active · registered',
      into: '55.00%',
    },
    { mark: '·', name: 'D. MARLOWE', role: 'Ultimate owner', status: 'sanctioned', stop: true, into: '100.00%' },
  ];

  return (
    <div className={styles.spec}>
      <SpecHead title={t('End-user verification · dual-use licence')} />
      <div className={styles.trace}>
        {steps.map((step) => (
          <div key={step.name}>
            {step.into && (
              <div className={styles.traceArrow}>
                <span>{step.into}</span>
              </div>
            )}
            <div className={cn(styles.traceNode, step.stop && styles.traceNodeStop)}>
              <Party code={step.code} mark={step.mark} />
              <span className={styles.traceBody}>
                <span className={styles.traceName}>{step.name}</span>
                <span className={styles.traceRole}>{step.role}</span>
              </span>
              <span className={styles.traceStatus}>{step.status}</span>
            </div>
          </div>
        ))}
      </div>
      <div className={styles.specFoot}>
        <span className={styles.footV}>
          Ownership runs back to a <b>designated party</b>
        </span>
        <span className={styles.footRest}>
          remaining 45.00% held via SAMPLE CAPITAL S.à r.l. · 3 registers · {RETRIEVED}
        </span>
      </div>
    </div>
  );
};

const TIMELINE_YEARS = ['2022', '2023', '2024', '2025'] as const;
type TimelineCell = 'on' | 'off' | 'due';
type TimelineRow = { code: string; name: string; role: string; cells: TimelineCell[] };

const TimelineSpec = () => {
  const { t } = useInt();
  const rows: TimelineRow[] = [
    { code: 'de', name: 'SAMPLE ALTREN COMPOSITES GMBH', role: 'parent', cells: ['on', 'on', 'on', 'on'] },
    { code: 'nl', name: 'SAMPLE HOLDINGS B.V.', role: '100.00%', cells: ['on', 'on', 'on', 'on'] },
    { code: 'ie', name: 'SAMPLE MARINE (IE)', role: '100.00%', cells: ['off', 'on', 'on', 'on'] },
    { code: 'sg', name: 'SAMPLE PACIFIC TRADING PTE', role: '80.00%', cells: ['on', 'on', 'on', 'off'] },
    { code: 'lu', name: 'SAMPLE PORT SERVICES', role: 'branch', cells: ['on', 'on', 'on', 'due'] },
  ];

  return (
    <div className={styles.spec}>
      <SpecHead title={t('Group perimeter · accounts as filed')} />
      <div className={styles.timelineWrap}>
        <div className={styles.timeline}>
          <div className={styles.timelineHead}>
            <span>{t('Entity · filing year')}</span>
            {TIMELINE_YEARS.map((year) => (
              <span key={year}>{year}</span>
            ))}
          </div>
          {rows.map((row) => (
            <div className={styles.timelineRow} key={row.name}>
              <span className={styles.timelineEnt}>
                <Party code={row.code} />
                <span className={styles.timelineName}>{row.name}</span>
                <span className={styles.timelineRole}>{row.role}</span>
              </span>
              {row.cells.map((cell, i) => (
                <i className={styles[`cell_${cell}`]} key={TIMELINE_YEARS[i]} />
              ))}
            </div>
          ))}
        </div>
      </div>
      <div className={styles.specFoot}>
        <span className={styles.footV}>
          14 entities across <b>6 jurisdictions</b>
        </span>
        <span className={styles.footRest}>
          every figure carries its filing reference · 1 filing overdue 41 days · {RETRIEVED}
        </span>
      </div>
    </div>
  );
};

type ThresholdLeg = { code: string; name: string; path: string; percent: string };

const ThresholdSpec = () => {
  const { t } = useInt();
  const legend: ThresholdLeg[] = [
    { code: 'nl', name: 'SAMPLE NORTH HOLDINGS B.V.', path: 'path A', percent: '34.00%' },
    { code: 'ae', name: 'SAMPLE GULF TRADING FZE', path: 'path B', percent: '16.00%' },
    { code: 'ch', name: 'SAMPLE ALPINE TRUST SA', path: 'path C', percent: '9.90%' },
  ];

  return (
    <div className={styles.spec}>
      <SpecHead title={t('Beneficial ownership · declared against register')} />
      <div className={styles.threshold}>
        <div className={styles.thresholdDec}>
          SAMPLE MARINE LOGISTICS LTD · declared <b>1 UBO</b> · no single path above 25%
        </div>
        <div className={styles.thresholdBarWrap}>
          <div className={styles.thresholdBar}>
            <i className={styles.segA} style={{ width: '34%' }} />
            <i className={styles.segB} style={{ width: '16%' }} />
            <i className={styles.segC} style={{ width: '9.9%' }} />
          </div>
          <span className={styles.thresholdMark} style={{ left: '50%' }}>
            <span>50% control test</span>
          </span>
        </div>
        <div className={styles.thresholdLegend}>
          {legend.map((row) => (
            <div key={row.name}>
              <Party code={row.code} />
              <span className={styles.legName}>{row.name}</span>
              <span className={styles.legRole}>{row.path}</span>
              <span className={styles.legPc}>{row.percent}</span>
            </div>
          ))}
        </div>
      </div>
      <div className={styles.specFoot}>
        <span className={styles.footV}>
          D. MARLOWE aggregates to <b>59.90%</b>, undeclared
        </span>
        <span className={styles.footRest}>held through three vehicles · 4 registers</span>
      </div>
    </div>
  );
};

type LedgerRow = { ref: string; code: string; name: string; amount: string };

const LedgerSpec = () => {
  const { t } = useInt();
  const rows: LedgerRow[] = [
    { ref: 'APP-4471', code: 'gb', name: 'SAMPLE HARBOUR CIVILS LTD', amount: '2,400,000' },
    { ref: 'APP-4488', code: 'gb', name: 'SAMPLE LOWLAND UTILITIES LTD', amount: '1,900,000' },
    { ref: 'APP-4502', code: 'ie', name: 'SAMPLE MARINE (IE)', amount: '800,000' },
  ];

  return (
    <div className={styles.spec}>
      <SpecHead title={t('Grant applications · common owner check')} />
      <div className={styles.ledger}>
        {rows.map((row) => (
          <div className={styles.ledgerRow} key={row.ref}>
            <span className={styles.ledgerRef}>{row.ref}</span>
            <Party code={row.code} />
            <span className={styles.ledgerName}>{row.name}</span>
            <span className={styles.ledgerAmt}>{row.amount}</span>
          </div>
        ))}
        <div className={styles.ledgerTotal}>
          <span>Aggregate exposure to one economic actor</span>
          <span className={styles.ledgerAmt}>£5,100,000</span>
        </div>
      </div>
      <div className={styles.specFoot}>
        <span className={styles.footV}>
          Three applicant names, <b>one common owner</b>
        </span>
        <span className={styles.footRest}>
          SAMPLE HOLDINGS B.V. · 3 of 3 · no shared name, address or director · 3 registers
        </span>
      </div>
    </div>
  );
};

type ManifestRow = { no: string; name: string; key: string; width: string; rows: string };

const ManifestSpec = () => {
  const { t } = useInt();
  const rows: ManifestRow[] = [
    { no: '01', name: 'Companies House', key: 'company_number', width: '100%', rows: '5,412,880' },
    { no: '02', name: 'Registre National des Entreprises', key: 'siren', width: '76%', rows: '4,118,206' },
    { no: '03', name: 'Handelsregister', key: 'hrb_number', width: '72%', rows: '3,904,117' },
    { no: '04', name: 'KVK Handelsregister', key: 'kvk_number', width: '43%', rows: '2,301,944' },
  ];

  return (
    <div className={styles.spec}>
      <SpecHead title={t('Dataset delivery · full-country extract')} />
      <div className={styles.manifestWrap}>
        <div className={styles.manifest}>
          <div className={styles.manifestHead}>
            <span />
            <span>{t('Source register')}</span>
            <span>{t('Primary key')}</span>
            <span />
            <span>{t('Rows')}</span>
          </div>
          {rows.map((row) => (
            <div className={styles.manifestRow} key={row.no}>
              <span className={styles.manifestNo}>{row.no}</span>
              <span className={styles.manifestName}>{row.name}</span>
              <span className={styles.manifestKey}>{row.key}</span>
              <span className={styles.manifestVol} style={{ '--w': row.width } as CSSProperties} />
              <span className={styles.manifestRows}>{row.rows}</span>
            </div>
          ))}
        </div>
      </div>
      <div className={styles.specFoot}>
        <span className={styles.footV}>
          Keyed on <b>the register{"'"}s own identifier</b>
        </span>
        <span className={styles.footRest}>
          one schema across 200+ countries · no proprietary key · redistribution licensed
        </span>
      </div>
    </div>
  );
};

type Mandate = {
  id: string;
  title: string;
  subtitle: string;
  panel: ReactNode;
  paragraph: ReactNode;
};

const getMandates = (t: Translate): Mandate[] => [
  {
    id: 'contracting',
    title: t('Contracting authorities'),
    subtitle: 'Procurement Act 2023',
    panel: <ExclusionSpec />,
    paragraph: (
      <>
        {t('Establish exclusion grounds not only for the bidder but for')}{' '}
        <b>{t('connected persons, parent undertakings and named sub-contractors')}</b>{' '}
        {t('— including those incorporated where you have no read access.')}
      </>
    ),
  },
  {
    id: 'screening',
    title: t('Investment screening bodies'),
    subtitle: 'NSIA 2021',
    panel: <LadderSpec />,
    paragraph: (
      <>
        {t(
          "Resolve the acquirer's ownership chain to ultimate control, layer by layer, so the notification test lands on",
        )}{' '}
        <b>{t('the entity holding the influence')}</b> {t('rather than the one that filed the form.')}
      </>
    ),
  },
  {
    id: 'export',
    title: t('Export control & trade licensing'),
    subtitle: t('Dual-use licence'),
    panel: <TraceSpec />,
    paragraph: (
      <>
        {t('Verify a stated end-user is a real, active, registered legal entity, and that its ownership')}{' '}
        <b>{t('does not run back to a designated party')}</b> {t('through an intermediate jurisdiction.')}
      </>
    ),
  },
  {
    id: 'tax',
    title: t('Tax authorities & customs'),
    subtitle: t('Accounts as filed'),
    panel: <TimelineSpec />,
    paragraph: (
      <>
        {t("Reconstruct a group's corporate footprint from primary filings in every jurisdiction it operates in, with")}{' '}
        <b>{t('financials as filed')}</b> {t('rather than as re-keyed by a vendor.')}
      </>
    ),
  },
  {
    id: 'aml',
    title: t('Financial intelligence & AML supervision'),
    subtitle: t('AML supervision'),
    panel: <ThresholdSpec />,
    paragraph: (
      <>
        {t('Test declared beneficial ownership against what the source registries hold, and work')}{' '}
        <b>{t('the whole graph')}</b> {t("rather than one country's slice of it.")}
      </>
    ),
  },
  {
    id: 'grants',
    title: t('Subsidy, grant & development agencies'),
    subtitle: t('Common owner check'),
    panel: <LedgerSpec />,
    paragraph: (
      <>
        {t('Identify the economic actor behind an applicant, aggregate awards across a corporate group, and detect')}{' '}
        <b>{t('the same ultimate owner applying through several vehicles')}</b>.
      </>
    ),
  },
  {
    id: 'statistics',
    title: t('Statistics offices & policy analysis'),
    subtitle: t('Full-country extract'),
    panel: <ManifestSpec />,
    paragraph: (
      <>
        {t(
          'Take a licensed, redistributable dataset of registered companies with open identifiers and analyse it inside your own environment,',
        )}{' '}
        <b>{t('with no vendor in the middle')}</b>.
      </>
    ),
  },
];

const N = 7;

export const GovernmentMandates = () => {
  const { t } = useInt();
  const mandates = getMandates(t);
  const [active, setActive] = useState(0);
  const activeRef = useRef(0);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const scrollWrapRef = useRef<HTMLDivElement>(null);
  const stickyRef = useRef<HTMLDivElement>(null);
  const programmaticRef = useRef(false);
  const [ind, setInd] = useState({ top: 0, height: 0 });

  useEffect(() => {
    activeRef.current = active;
  }, [active]);

  useIsomorphicLayoutEffect(() => {
    const el = itemRefs.current[active];
    if (el) {
      setInd({ top: el.offsetTop, height: el.offsetHeight });
    }
  }, [active]);

  const stepTop = (index: number) => {
    const wrapEl = scrollWrapRef.current;
    const stickyEl = stickyRef.current;
    if (!wrapEl || !stickyEl) {
      return 0;
    }

    const travel = Math.max(1, wrapEl.offsetHeight - stickyEl.offsetHeight);
    const stickyTop = parseFloat(getComputedStyle(stickyEl).top);
    const top = Number.isNaN(stickyTop) ? 96 : stickyTop;
    return window.scrollY + wrapEl.getBoundingClientRect().top + (index / N) * travel - top + 8;
  };

  const pick = (index: number, jump = false) => {
    setActive(index);
    itemRefs.current[index]?.focus();

    if (jump && scrollWrapRef.current && stickyRef.current) {
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      programmaticRef.current = true;
      window.scrollTo({ top: stepTop(index), behavior: reduce ? 'auto' : 'smooth' });

      if (reduce) {
        programmaticRef.current = false;
      } else {
        let lastY = window.scrollY;
        let stableFrames = 0;
        const settleCheck = () => {
          const y = window.scrollY;
          if (Math.abs(y - lastY) < 0.5) {
            stableFrames += 1;
          } else {
            stableFrames = 0;
          }
          lastY = y;

          if (stableFrames >= 3) {
            programmaticRef.current = false;
            return;
          }
          requestAnimationFrame(settleCheck);
        };
        requestAnimationFrame(settleCheck);
      }
    }
  };

  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    let next: number | null = null;
    if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
      next = (index + 1) % N;
    }
    if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
      next = (index - 1 + N) % N;
    }
    if (next !== null) {
      event.preventDefault();
      pick(next, true);
    }
  };

  useEffect(() => {
    const wrapEl = scrollWrapRef.current;
    const stickyEl = stickyRef.current;
    if (!wrapEl || !stickyEl || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return undefined;
    }

    let running = true;

    const poll = () => {
      if (!running) {
        return;
      }

      if (!programmaticRef.current && getComputedStyle(stickyEl).position === 'sticky') {
        const parsedTop = parseFloat(getComputedStyle(stickyEl).top);
        const stickyTop = Number.isNaN(parsedTop) ? 96 : parsedTop;
        const travel = Math.max(1, wrapEl.offsetHeight - stickyEl.offsetHeight);
        const passed = stickyTop - wrapEl.getBoundingClientRect().top;
        const pct = Math.min(0.9999, Math.max(0, passed / travel));
        const index = Math.floor(pct * N + 1e-6);

        if (index >= 0 && index < N && index !== activeRef.current) {
          setActive(index);
        }
      }

      requestAnimationFrame(poll);
    };

    const frame = requestAnimationFrame(poll);

    return () => {
      running = false;
      cancelAnimationFrame(frame);
    };
  }, []);

  const mandate = mandates[active];

  return (
    <Section className={styles.root}>
      <div className={styles.head}>
        <TitleBlock
          title={t('Seven mandates. One shared blind spot.')}
          description={t(
            'Each of these functions is required by statute to establish something about a company that its own registry cannot answer.',
          )}
          align="left"
        />
      </div>

      <div className={styles.scrollWrap} ref={scrollWrapRef} style={{ '--steps': N } as CSSProperties}>
        <div className={styles.sticky} ref={stickyRef}>
          <div className={styles.grid}>
            <div className={styles.navList} role="tablist" aria-label="Government mandates">
              <span
                className={styles.navPill}
                style={{ transform: `translateY(${ind.top}px)`, height: `${ind.height}px` }}
              />
              {mandates.map((item, i) => (
                <button
                  type="button"
                  role="tab"
                  aria-selected={active === i}
                  id={`mand-tab-${item.id}`}
                  aria-controls={`mand-panel-${item.id}`}
                  className={cn(styles.navItem, active === i && styles.navItemOn, i < active && styles.navItemDone)}
                  ref={(el) => {
                    itemRefs.current[i] = el;
                  }}
                  onClick={() => pick(i, true)}
                  onKeyDown={(event) => onKeyDown(event, i)}
                  key={item.id}
                >
                  <span className={styles.no}>{i < active ? '✓' : String(i + 1).padStart(2, '0')}</span>
                  <span className={styles.navBody}>
                    <span className={styles.mn}>{item.title}</span>
                    <span className={styles.ms}>{item.subtitle}</span>
                  </span>
                </button>
              ))}
            </div>

            <div className={styles.panelWrap}>
              <div
                className={styles.panel}
                id={`mand-panel-${mandate.id}`}
                role="tabpanel"
                aria-labelledby={`mand-tab-${mandate.id}`}
                key={mandate.id}
              >
                <span className={styles.step}>
                  {String(active + 1).padStart(2, '0')} / {String(N).padStart(2, '0')}
                </span>
                <h3 className={styles.panelTitle}>{mandate.title}</h3>
                {mandate.panel}
                <p className={styles.panelBody}>{mandate.paragraph}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Section>
  );
};
